import type {
    ApplyV2FileAttachment,
    ApplyV2TempBase64File,
    ApplyV2TempCourseFile,
    V2CourseFile,
} from '@core/app/types/applyV2Type'
import NotSupportedFormatMask from '@core/components/ApplyDialogV2/ApplyV2AttachmentsUploader/NotSupportedFormatMask'
import UploadCvIcon from '@core/components/ApplyDialogV2/assets/UploadCvIcon'
import DefaultMaskIcon from '@core/components/ApplyDialogV2/FileUploaderV2/DefaultMaskIcon'
import UnsupportedFileMaskIcon from '@core/components/ApplyDialogV2/FileUploaderV2/UnsupportedFileMaskIcon'
import FileUploaderV2ListItem from '@core/components/ApplyDialogV2/FileUploaderV2ListItem'
import WarningErrorIcon from '@core/components/icons/WarningErrorIcon'
import useApplyV2NavTranslations from '@core/utils/hooks/useApplyV2NavTranslations'
import { progress } from '@motionone/utils'
import clsx from 'clsx'
import React, { useCallback, useId, useLayoutEffect, useMemo, useRef, useState } from 'react'
import './ApplyV2AttachmentsUploader.scss'

interface Props {
    id?: string | number
    label?: React.ReactNode
    rightLabel?: React.ReactNode
    name?: string
    files?: (V2CourseFile | ApplyV2TempBase64File)[] | null
    className?: string
    onDelete?: (file: V2CourseFile | File | ApplyV2TempBase64File) => void | Promise<void>
    isShowDelete?: boolean
    onChange?: (files: ApplyV2TempBase64File[]) => void
    // onChange?: React.ChangeEventHandler<HTMLInputElement>
    // onChangeFiles?: (files: File[]) => void
    onFocus?: React.FocusEventHandler<HTMLInputElement>
    onBlur?: React.FocusEventHandler<HTMLInputElement>
    errorMessage?: string | string[] | null | boolean | undefined
    isLoading?: boolean
    isDisabled?: boolean
    children?: React.ReactNode
    isShowErrorMessage?: boolean
    description?: React.ReactNode
    isRequired?: boolean
    isShowInput?: boolean
    isShowList?: boolean
    accept?: string[]
    maxFileSize?: number
    multipleCount?: number
    // isBase64: boolean
    loadingIds?: (string | number)[]
    uploadingCount?: number
    filesErrors?: ({ error: string } & (V2CourseFile | ApplyV2TempBase64File))[]
    progressItems?: { index: number; progress: number }[]
}

const baseClass = 'ApplyV2AttachmentsUploader'

function ApplyV2AttachmentsUploader({
    files = [],
    className,
    id,
    label,
    rightLabel,
    name,
    onDelete,
    onChange,
    onFocus,
    onBlur,
    isShowDelete = false,
    errorMessage,
    isLoading = false,
    isDisabled = false,
    children,
    isShowErrorMessage = true,
    description,
    isRequired = false,
    isShowInput = true,
    isShowList = true,
    accept = [],
    maxFileSize = 20 * 1024 * 1024,
    multipleCount = 1,
    // isBase64 = true,
    loadingIds = [],
    uploadingCount = 0,
    filesErrors = [],
    progressItems = [],
}: Props) {
    const containerRef = useRef<HTMLDivElement>(null)
    const usedId = useId()
    const { getTitle } = useApplyV2NavTranslations()
    const inputRef = useRef<HTMLInputElement>(null)
    const [innerError, setInnerError] = useState('')
    const [isUnsupportedFormat, setIsUnsupportedFormat] = useState(false)
    const [isOnDrag, setIsOnDrag] = useState(false)
    const [isNarrow, setIsNarrow] = useState(false)

    const errorMessageRes = useMemo(() => {
        const res = Array.isArray(errorMessage) ? errorMessage : [errorMessage]
        return res.filter(Boolean).join(', ')
    }, [errorMessage])

    const defaultId = id ? id.toString() : usedId
    useLayoutEffect(() => {
        const checkWidth = () => {
            if (containerRef.current) {
                setIsNarrow(containerRef.current.offsetWidth < 500)
            }
        }

        checkWidth()
        window.addEventListener('resize', checkWidth)

        return () => {
            window.removeEventListener('resize', checkWidth)
        }
    }, [])

    const isFileAccepted = (file: File): boolean => {
        if (!accept.length) return true
        const fileExtension = file.name.split('.').pop()?.toLowerCase() || ''
        return accept.some(
            (ext) =>
                ext.toLowerCase() === fileExtension ||
                ext.toLowerCase() === `.${fileExtension}` ||
                ext.toLowerCase() === file.type
        )
    }

    const validateFiles = (fils: File[]): string | null => {
        const errors = []
        if ((files ?? []).length + fils.length > multipleCount) {
            errors.push(`${multipleCount} files allowed`)
        }

        if (accept.length > 0) {
            const invalidFiles = fils.filter((file) => !isFileAccepted(file))
            if (invalidFiles.length > 0) {
                errors.push(getTitle('6200', 'File format is not supported'))
            }
        }

        const oversizedFiles = fils.filter((file) => file.size > maxFileSize * 1024 * 1024)
        if (oversizedFiles.length > 0) {
            console.log('oversized files', oversizedFiles)
            errors.push(`Some files exceed maximum size, - ${maxFileSize} MB`)
        }

        return errors.join(', ')
    }

    const onDragEnter = (e: React.DragEvent<HTMLInputElement>) => {
        console.log('onDragEnter')
        setIsOnDrag(true)
        const items = e.dataTransfer.items
        const invalidFiles: string[] = []

        for (let i = 0; i < items.length; i++) {
            const item = items[i]
            if (item.kind === 'file') {
                const mimeType = item.type
                if (mimeType) {
                    const mainType = mimeType.split('/')[0]
                    if (!accept.some((ext) => mimeType.includes(ext.replace('.', '')))) {
                        invalidFiles.push(`INVALID FORMAT`)
                    }
                }
            }
        }

        console.log('onDragEnter', invalidFiles)

        if (invalidFiles.length > 0) {
            setInnerError(getTitle('6200', 'File format is not supported'))
        } else {
            setInnerError('')
        }
    }
    const onDragLeave = (e: React.DragEvent<HTMLInputElement>) => {
        console.log('onDragLeave')
        setIsOnDrag(false)
        setInnerError('')
    }
    const onDrop = (e: React.DragEvent<HTMLInputElement>) => {
        console.log('onDrop')
        setIsOnDrag(false)
        setInnerError('')

        if (inputRef?.current) {
            inputRef.current.value = ''
        }
    }

    const onFocusMiddleware = (e: React.FocusEvent<HTMLInputElement>) => {
        console.log('onFocusMiddleware')
        // setInnerError('')
        onFocus && onFocus(e)
        setInnerError('')
    }

    const onBlurMiddleware = (e: React.FocusEvent<HTMLInputElement>) => {
        console.log('onBlurMiddleware')
        // setInnerError('')
        onBlur && onBlur(e)
    }

    const fileToBase64 = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader()
            reader.onload = () => {
                const result = reader.result as string
                const base64 = result.split('base64,')[1]
                resolve(base64)
            }
            reader.onerror = reject
            reader.readAsDataURL(file)
        })
    }

    const convertToBase64Promise = async (
        files: File[]
    ): Promise<{ name: string; ext: string; base64: string; lastModified: number; size: number }[]> => {
        return Promise.all(
            files.map(async (file) => {
                const base64 = await fileToBase64(file)
                const ext = file.name.split('.').pop() || file.type.split('/').pop() || ''
                return {
                    name: file.name,
                    ext,
                    base64,
                    lastModified: file.lastModified,
                    size: file.size,
                }
            })
        )
    }

    const onDeleteMiddleware = (
        file: File | ApplyV2TempCourseFile | ApplyV2FileAttachment | V2CourseFile | ApplyV2TempBase64File
    ) => {
        // console.log('onDeleteMiddleware')
        // console.log(file)

        if ('uid' in file && 'ext' in file && onDelete) {
            onDelete(file)
        } else if (file instanceof File) {
            console.log('Deleting File object:', file.name)
            // onDelete && onDelete(file as File)
            onDelete && onDelete(file)
        } else {
            console.log('Deleting other file type')
            onDelete && onDelete(file)
        }
        console.log(innerError, 'delete')
        setInnerError('')
    }

    const onChangeMiddleware = (e: React.ChangeEvent<HTMLInputElement>) => {
        console.log('onChangeMiddleware')
        setInnerError('')
        setIsOnDrag(false)
        try {
            const filesArray = Array.from(e.target.files ?? [])
            const validationError = validateFiles(filesArray)
            console.log('validationError', validationError)
            if (validationError) {
                setInnerError(validationError)
                return
            }

            convertToBase64Promise(filesArray).then((res) => {
                onChange && onChange(res)
            })
        } catch (error) {
            console.error('Error handling file upload:', error)
            setInnerError('Failed to process files')
        } finally {
            e.target.value = ''
        }
    }

    return (
        <div
            ref={containerRef}
            className={clsx(baseClass, className, {
                [`${baseClass}--error`]: !!errorMessageRes || !!innerError,
                [`${className}--error`]: (!!errorMessageRes || !!innerError) && !!className,
                [`${baseClass}--disabled`]: isDisabled,
                [`${className}--disabled`]: !!className && isDisabled,
                [`${baseClass}--loading`]: isLoading,
                [`${className}--loading`]: !!className && isLoading,
                [`${baseClass}--inputHidden`]: !isShowInput,
                [`${baseClass}--inputHidden`]: !isShowInput && !!className,
            })}
        >
            {(label || rightLabel) && (
                <div
                    className={clsx(`${baseClass}__label`, {
                        [`${className}__label`]: !!className,
                    })}
                >
                    <label
                        className={clsx(`${baseClass}__label--text`, {
                            [`${className}__label--text`]: !!className,
                        })}
                        htmlFor={id ? id.toString() : defaultId}
                    >
                        {label}
                        {isRequired && '*'}
                    </label>
                    {rightLabel && <span>{rightLabel}</span>}
                    {/*<span*/}
                    {/*    className={clsx(`${baseClass}__label--text`, {*/}
                    {/*        [`${className}__label--text`]: !!className,*/}
                    {/*    })}*/}
                    {/*>*/}
                    {/*    {label}*/}
                    {/*    {isRequired && '*'}*/}
                    {/*</span>*/}
                </div>
            )}
            {isShowInput && (
                <div
                    className={clsx(`${baseClass}__wrapper`, {
                        [`${className}__wrapper`]: !!className,
                        [`${baseClass}__wrapper--error`]: !!errorMessageRes || !!innerError,
                        [`${className}__wrapper--error`]: (!!errorMessageRes || !!innerError) && !!className,
                        [`${baseClass}__wrapper--disabled`]: isDisabled,
                        [`${className}__wrapper--disabled`]: !!className && isDisabled,
                    })}
                >
                    {isLoading && (
                        <div
                            className={clsx(`${baseClass}__wrapper__loader`, {
                                [`${className}__wrapper__loader`]: className && isLoading,
                            })}
                        ></div>
                    )}
                    <div
                        className={clsx(`${baseClass}__wrapper__content`, {
                            [`${className}__wrapper__content`]: !!className,
                            [`${baseClass}__wrapper__content--disabled`]: !isDisabled,
                            [`${className}__wrapper__content--disabled`]: !!isDisabled && !!className,
                            [`${baseClass}__wrapper__content--error`]: !!innerError || !!errorMessageRes,
                            [`${className}__wrapper__content--error`]:
                                (!!innerError || !!errorMessageRes) && !!className,
                        })}
                        // onClick={(e) => {
                        //     // handleClick()
                        // }}
                    >
                        <input
                            ref={inputRef}
                            id={defaultId}
                            type="file"
                            onError={(e) => {
                                console.log('Input FILE error')
                            }}
                            onDragEnter={onDragEnter}
                            onDragLeave={onDragLeave}
                            // onDragOver={(e) => {
                            //     console.log('onDragOver')
                            // }}
                            onDrop={onDrop}
                            name={name}
                            multiple={multipleCount > 1}
                            accept={accept?.join(',')}
                            onChange={onChangeMiddleware}
                            onFocus={onFocusMiddleware}
                            onBlur={onBlurMiddleware}
                            disabled={isDisabled}
                            className={clsx(`${baseClass}__wrapper__input`, {
                                [`${baseClass}__wrapper__input--error`]: !!errorMessageRes || !!innerError,
                                [`${className}__wrapper__input--error`]:
                                    className && (!!errorMessageRes || !!innerError),
                                [`${baseClass}__wrapper__input--loading`]: isLoading,
                                [`${className}__wrapper__input--loading`]: className && isLoading,
                                [`${baseClass}__wrapper__input--isOnDrag`]: isOnDrag,
                                [`${className}__wrapper__input--isOnDrag`]: className && isOnDrag,
                            })}
                        />
                        {innerError || errorMessageRes ? (
                            <div className={`${baseClass}__inner`}>
                                <div className={`${baseClass}__inner__icon ${baseClass}__inner__icon-error`}>
                                    {/*<UnsupportedFileMaskIcon />*/}
                                    <NotSupportedFormatMask />
                                    {/*<WarningErrorIcon />*/}
                                    {/*<i className="fa-light fa-circle-exclamation" style={{ fontSize: '64px' }}></i>*/}
                                </div>
                                <p className={`${baseClass}__inner__text`}>
                                    {innerError || errorMessageRes || getTitle('6200', 'File format is not supported')}
                                </p>
                            </div>
                        ) : (
                            <div className={`${baseClass}__inner`}>
                                {children || (
                                    <>
                                        <div className={`${baseClass}__inner__icon`}>
                                            <DefaultMaskIcon />
                                        </div>
                                        <p className={`${baseClass}__inner__text`}>
                                            {isOnDrag ? 'dwa' : getTitle('10348', 'Drop files here or click to upload')}
                                        </p>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {description && (
                <div
                    className={clsx(`${baseClass}__description`, {
                        [`${className}__description`]: className,
                    })}
                >
                    {description}
                </div>
            )}
            {errorMessageRes && isShowErrorMessage && (
                <div
                    className={clsx(`${baseClass}__errorMessage`, {
                        [`${className}__errorMessage`]: !!className,
                    })}
                >
                    <div
                        className={clsx(`${baseClass}__errorMessage--icon`, {
                            [`${className}__errorMessage--icon`]: className,
                        })}
                    >
                        <WarningErrorIcon />
                    </div>
                    <div
                        className={clsx(`${baseClass}__errorMessage--text`, {
                            [`${className}__errorMessage--text`]: className,
                        })}
                    >
                        {errorMessageRes}
                    </div>
                </div>
            )}
            {files && files.length + uploadingCount > 0 && (
                <div
                    className={clsx(`${baseClass}__files`, {
                        [`${className}__files`]: !!className,
                    })}
                >
                    {files.map((file, index, array) => {
                        const error = (filesErrors ?? []).find((err) => {
                            if ('uid' in file && 'uid' in err) {
                                return err.uid === file.uid
                            }
                            return err.name === file.name
                        })?.error
                        console.log('err', error)
                        const isLast = index === array.length - 1 + uploadingCount
                        const isOdd = (array.length + uploadingCount) % 2 === 1
                        const classNameItem = clsx(`${baseClass}__files__item`, {
                            [`${className}__files__item`]: className,
                            [`${baseClass}__files--narrow`]: isNarrow,
                            [`${className}__files--narrow`]: isNarrow && className,
                            [`${baseClass}__files__item--full`]: isLast && isOdd,
                            [`${className}__files__item--full`]: isLast && isOdd && className,
                            [`${baseClass}__files__item--last`]: isLast,
                            [`${className}__files__item--last`]: isLast && className,
                            [`${baseClass}__files__item--error`]: error,
                            [`${className}__files__item--error`]: error && className,
                            [`${baseClass}__files__item--show-delete`]: isShowDelete,
                            [`${className}__files__item--show-delete`]: isShowDelete && className,
                        })
                        // console.log(
                        //     // 'isLast: ',
                        //     // isLast,
                        //     // 'isOdd:',
                        //     // (array.length + uploadingCount) % 2,        // 'isLast: ',
                        //     // isLast,
                        //     // 'isOdd:',
                        //     // (array.length + uploadingCount) % 2,
                        //     '%c' + file.name, // Apply styling to file.name
                        //     'color: #4CAF50; font-weight: bold;',
                        //     'index: ',
                        //     index,
                        //     'isLast',
                        //     array.length - 1 + uploadingCount,
                        //     array.length - 1 + uploadingCount === index
                        // )
                        return (
                            <FileUploaderV2ListItem
                                key={'uid' in file ? `uid-${file.uid}-${error}` : `name-${file.name}-${error}`}
                                file={file}
                                className={classNameItem}
                                fileError={error}
                                // onDelete={onDelete}
                                progress={(progressItems ?? []).find((it) => it.index === index)?.progress}
                                status={(progressItems ?? []).find((it) => it.index === index) && 'uploading'}
                                isShowDelete={!!onDelete}
                                loading={
                                    'uid' in file
                                        ? loadingIds?.includes(file.uid)
                                        : 'name' in file
                                          ? loadingIds?.includes(file.name)
                                          : false
                                }
                                onDelete={onDeleteMiddleware}
                            />
                        )
                    })}
                    {Array.from({ length: uploadingCount }).map((_, index) => {
                        const isLast = index === files.length + uploadingCount
                        const isOdd = (files.length + uploadingCount) % 2 === 1
                        return (
                            <FileUploaderV2ListItem
                                key={`loading-${index}`}
                                file={{ name: '', size: 0, type: '', uid: `loading-${index}` } as unknown as File}
                                loading={true}
                                onDelete={onDeleteMiddleware}
                                className={clsx(`${baseClass}__files__item ${baseClass}__files__item--loading`, {
                                    [`${baseClass}__files__item--full`]: isLast && isOdd,
                                    [`${baseClass}__files__item--last`]: isLast,
                                    [`${className}__files__item`]: className,
                                    [`${className}__files__item--full`]: isLast && isOdd && className,
                                    [`${className}__files__item--last`]: isLast && className,
                                })}
                            />
                        )
                    })}
                </div>
            )}
        </div>
    )
}

export default ApplyV2AttachmentsUploader
